import { lazy } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router'
import { RequireOnboarded } from '@/components/auth/require-onboarded'
import { RequireSession } from '@/components/auth/require-session'
import type { ScreenHandle } from '@/components/console/frame/console-frame'
import { RequirePlatform } from '@/components/platform/require-platform'
import { RouteError } from '@/components/shared/route-error'
import { AdminLayout } from '@/layouts/admin-layout'
import { AuthLayout } from '@/layouts/auth-layout'
import { DashboardLayout } from '@/layouts/dashboard-layout'
import { OnboardingLayout } from '@/layouts/onboarding-layout'
import { NotFoundPage } from '@/pages/not-found'

// Route-level code splitting: each page is its own chunk.
const LoginPage = lazy(() => import('@/pages/auth/login'))
const SignupPage = lazy(() => import('@/pages/auth/signup'))
const VerifyEmailPage = lazy(() => import('@/pages/auth/verify-email'))
const OnboardingPage = lazy(() => import('@/pages/auth/onboarding'))
const DashboardOverviewPage = lazy(() => import('@/pages/dashboard/overview'))
const DashboardAgentsPage = lazy(() => import('@/pages/dashboard/agents/list'))
const DashboardNewAgentPage = lazy(() => import('@/pages/dashboard/agents/new'))
const DashboardAgentPage = lazy(() => import('@/pages/dashboard/agents/agent'))
const DashboardCallsPage = lazy(() => import('@/pages/dashboard/calls'))
const DashboardBookingsPage = lazy(() => import('@/pages/dashboard/bookings'))
const DashboardNumbersPage = lazy(() => import('@/pages/dashboard/numbers'))
const DashboardRoutingPage = lazy(() => import('@/pages/dashboard/routing'))
const DashboardAnalyticsPage = lazy(() => import('@/pages/dashboard/analytics'))
const DashboardAskMiyaPage = lazy(() => import('@/pages/dashboard/ask-miya'))
const DashboardKnowledgePage = lazy(() => import('@/pages/dashboard/knowledge'))
const DashboardSettingsPage = lazy(() => import('@/pages/dashboard/settings'))
const DashboardBillingPage = lazy(() => import('@/pages/dashboard/billing'))
const AdminOverviewPage = lazy(() => import('@/pages/admin/overview'))
const AdminAccountsPage = lazy(() => import('@/pages/admin/accounts'))
const AdminAccountDetailPage = lazy(() => import('@/pages/admin/account-detail'))
const AdminCallsPage = lazy(() => import('@/pages/admin/calls'))
const AdminLeadsPage = lazy(() => import('@/pages/admin/leads'))
const AdminBillingPage = lazy(() => import('@/pages/admin/billing'))
const AdminQueuePage = lazy(() => import('@/pages/admin/queue'))

const screen = (title: string): ScreenHandle => ({ title })

// Exported so a test can mount the real route tree in memory -- the nesting of the
// guards is itself something that can be wrong.
export const routes: RouteObject[] = [
  { path: '/', element: <Navigate to="/dashboard" replace />, errorElement: <RouteError /> },
  {
    Component: AuthLayout,
    errorElement: <RouteError />,
    children: [
      { path: 'login', Component: LoginPage },
      { path: 'signup', Component: SignupPage },
      { path: 'verify-email', Component: VerifyEmailPage },
    ],
  },
  {
    // The wizard's own full-page layout. Outside the guards, like verify-email: the
    // page does its own routing, and inside RequireOnboarded it would redirect to itself.
    Component: OnboardingLayout,
    errorElement: <RouteError />,
    children: [{ path: 'onboarding', Component: OnboardingPage }],
  },
  // Everything below the guard needs a session. The server is the authority --
  // this only avoids painting a console to someone who will get 401s anyway.
  {
    Component: RequireSession,
    errorElement: <RouteError />,
    children: [
      {
        // The tenant dashboard stays locked until the business profile is saved.
        // The platform console below is not: Ringout's own people (ADR-039) have
        // no business profile to fill.
        Component: RequireOnboarded,
        children: [
          {
            path: 'dashboard',
            Component: DashboardLayout,
            // Each screen's `handle.title` is what the console header calls it,
            // in the design's words; the sidebar order is components/console/frame/nav.ts.
            children: [
              { index: true, Component: DashboardOverviewPage, handle: screen('Overview') },
              {
                path: 'agents',
                children: [
                  { index: true, Component: DashboardAgentsPage, handle: screen('Agents') },
                  { path: 'new', Component: DashboardNewAgentPage, handle: screen('Agent builder') },
                  { path: ':slug', Component: DashboardAgentPage, handle: screen('Agent builder') },
                ],
              },
              { path: 'calls', Component: DashboardCallsPage, handle: screen('Calls') },
              { path: 'bookings', Component: DashboardBookingsPage, handle: screen('Bookings') },
              { path: 'numbers', Component: DashboardNumbersPage, handle: screen('Phone numbers') },
              { path: 'routing', Component: DashboardRoutingPage, handle: screen('Routing & handoffs') },
              { path: 'analytics', Component: DashboardAnalyticsPage, handle: screen('Analytics') },
              { path: 'ask-miya', Component: DashboardAskMiyaPage, handle: screen('Ask Miya') },
              { path: 'knowledge', Component: DashboardKnowledgePage, handle: screen('Knowledge base') },
              { path: 'settings', Component: DashboardSettingsPage, handle: screen('Settings') },
              { path: 'billing', Component: DashboardBillingPage, handle: screen('Billing') },
            ],
          },
        ],
      },
      {
        // The platform console. The guard asks the API whether this person holds a grant, and
        // paints nothing of the console (not even its navigation) until it says yes (ADR-039).
        path: 'admin',
        Component: RequirePlatform,
        children: [
          {
            Component: AdminLayout,
            children: [
              { index: true, Component: AdminOverviewPage },
              { path: 'accounts', Component: AdminAccountsPage },
              { path: 'accounts/:accountId', Component: AdminAccountDetailPage },
              { path: 'calls', Component: AdminCallsPage },
              { path: 'leads', Component: AdminLeadsPage },
              { path: 'billing', Component: AdminBillingPage },
              { path: 'queue', Component: AdminQueuePage },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', Component: NotFoundPage },
]

export const router = createBrowserRouter(routes)
